import { useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useToast } from './Toast';
import { t } from '../i18n';
import { messageOf } from '../lib/format';
import { Link } from 'react-router-dom';

interface FollowButtonProps {
  userId: string;
  following: boolean;
  onToggle?: (following: boolean) => void;
  size?: 'sm' | 'md';
}

export function FollowButton({ userId, following, onToggle, size = 'md' }: FollowButtonProps) {
  const { user } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  if (!user) {
    return (
      <Link className={`btn btn--outline btn--${size}`} to="/login">
        {t('ui.follow')}
      </Link>
    );
  }
  if (user.id === userId) return null;

  async function toggle() {
    setBusy(true);
    try {
      if (following) {
        await api.users.unfollow(userId);
        onToggle?.(false);
      } else {
        await api.users.follow(userId);
        onToggle?.(true);
        toast.success(t('ui.followingHint'));
      }
    } catch (error) {
      toast.error(messageOf(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      className={`btn ${following ? 'btn--outline' : 'btn--primary'} btn--${size}`}
      onClick={toggle}
      disabled={busy}
      aria-pressed={following}
    >
      {busy ? '…' : following ? t('ui.following') : t('ui.follow')}
    </button>
  );
}
